import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateQuestion = () => {
    const num1 = getRandomNumber(1, 9);
    const num2 = getRandomNumber(1, 9 - num1); // Ensure sum doesn't exceed 9
    const correctAnswer = num1 + num2;

    const options = new Set<number>();
    options.add(correctAnswer);
    while (options.size < 3) {
        options.add(getRandomNumber(2, 10));
    }

    return {
        question: `${num1} + ${num2} = ?`,
        correctAnswer,
        options: Array.from(options).sort(() => Math.random() - 0.5), // Shuffle options
    };
};

const AdditionVisual: React.FC = () => {
    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "Visual Addition for UKG | Learn Addition with Pictures & Objects";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setQuiz(generateQuestion());
                setShowImage(false);
                setFeedback("");
            }, 5000);
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help your UKG child master addition through visual aids. Explore free worksheets using pictures, counters & real-life objects. Fun, interactive, and effective!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/addition/visual-addition" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Visual Addition</h1>

                                <p>
                                    Addition becomes more exciting when kids can <strong>see what they are adding</strong>. Visual Addition uses images, counters, fingers, or real-world objects to help UKG learners understand the concept of combining two groups to make a total.
                                </p>

                                <h2>🔍 What Is Visual Addition?</h2>
                                <p>
                                    Visual Addition is a method of teaching addition using <strong>visual cues like objects, dots, images, or hands-on tools</strong>. It is ideal for early learners who are just starting to grasp the meaning of numbers and how they work together.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and count groups of objects</li>
                                    <li>Develop the ability to combine sets visually</li>
                                    <li>Lay the foundation for numeric addition later</li>
                                    <li>Enhance observation and critical thinking skills</li>
                                </ul>

                                <h2>🖼️ Visual Addition Examples</h2>
                                <p><strong>Example 1:</strong> 🐶🐶 + 🐶 = <strong>3 Dogs</strong></p>
                                <p><strong>Example 2:</strong> 🍓🍓🍓 + 🍓🍓 = <strong>5 Strawberries</strong></p>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Tips for Teaching Visual Addition at Home</h2>
                                <ul>
                                    <li>Use building blocks or toys to create physical addition stories</li>
                                    <li>Draw simple pictures and ask children to count totals</li>
                                    <li>Use finger counting for small additions (1–5)</li>
                                    <li>Make it a game – <strong>“How many apples if I add 2 more?”</strong></li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Visual Addition</h1>

                            <p>
                                Addition becomes more exciting when kids can <strong>see what they are adding</strong>. Visual Addition uses images, counters, fingers, or real-world objects to help UKG learners understand the concept of combining two groups to make a total.
                            </p>

                            <h2>🔍 What Is Visual Addition?</h2>
                            <p>
                                Visual Addition is a method of teaching addition using <strong>visual cues like objects, dots, images, or hands-on tools</strong>. It is ideal for early learners who are just starting to grasp the meaning of numbers and how they work together.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and count groups of objects</li>
                                <li>Develop the ability to combine sets visually</li>
                                <li>Lay the foundation for numeric addition later</li>
                                <li>Enhance observation and critical thinking skills</li>
                            </ul>

                            <h2>🖼️ Visual Addition Examples</h2>
                            <p><strong>Example 1:</strong> 🐶🐶 + 🐶 = <strong>3 Dogs</strong></p>
                            <p><strong>Example 2:</strong> 🍓🍓🍓 + 🍓🍓 = <strong>5 Strawberries</strong></p>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Tips for Teaching Visual Addition at Home</h2>
                            <ul>
                                <li>Use building blocks or toys to create physical addition stories</li>
                                <li>Draw simple pictures and ask children to count totals</li>
                                <li>Use finger counting for small additions (1–5)</li>
                                <li>Make it a game – <strong>“How many apples if I add 2 more?”</strong></li>
                            </ul>
                        </article>
                        <h2>{quiz.question}</h2>
                        <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                            {quiz.options.map((option, index) => (
                                <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                    {option}
                                </div>
                            ))}
                        </div>
                        {feedback && <p>{feedback}</p>}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELLDONE_LINK} />
                    </div>
                </>
            )}
        </>
    );
};

export default AdditionVisual;
